import React, { createContext, useContext, useState, useCallback, useRef } from "react";

const STORAGE_KEY = "habitia_tutorials_completed";

const TutorialContext = createContext(null);

const getCompleted = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const markCompleted = (tutorialId) => {
  try {
    const completed = getCompleted();
    if (!completed.includes(tutorialId)) {
      completed.push(tutorialId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(completed));
    }
  } catch { /* ignorar */ }
};

export const TutorialProvider = ({ children }) => {
  const [activeTutorial, setActiveTutorial] = useState(null);
  // Registro de acciones: { actionId: callbackFn }
  const actionsRef = useRef({});

  /**
   * Registra una accion que el tutorial puede invocar al llegar a un paso.
   * Usar desde componentes de pantalla para vincular acciones como abrir modales.
   */
  const registerAction = useCallback((actionId, callback) => {
    actionsRef.current[actionId] = callback;
  }, []);

  const unregisterAction = useCallback((actionId) => {
    delete actionsRef.current[actionId];
  }, []);

  // Ejecuta la accion de un paso si tiene una
  const executeStepAction = useCallback((step) => {
    if (step && step.action && actionsRef.current[step.action]) {
      actionsRef.current[step.action]();
    }
  }, []);

  const startTutorial = useCallback((tutorial, force = false) => {
    if (!tutorial || !tutorial.steps || tutorial.steps.length === 0) return;
    if (!force && getCompleted().includes(tutorial.id)) return;
    const firstStep = tutorial.steps[0];
    // Ejecutar accion del primer paso
    if (firstStep && firstStep.action && actionsRef.current[firstStep.action]) {
      actionsRef.current[firstStep.action]();
    }
    setActiveTutorial({ ...tutorial, currentStep: 0 });
  }, []);

  const nextStep = useCallback(() => {
    setActiveTutorial((prev) => {
      if (!prev) return null;
      const next = prev.currentStep + 1;
      if (next >= prev.steps.length) {
        // Ejecutar accion de cleanup del ultimo paso
        const lastStep = prev.steps[prev.currentStep];
        if (lastStep && lastStep.cleanupAction && actionsRef.current[lastStep.cleanupAction]) {
          actionsRef.current[lastStep.cleanupAction]();
        }
        markCompleted(prev.id);
        return null;
      }
      // Ejecutar accion del nuevo paso
      const newStep = prev.steps[next];
      if (newStep && newStep.action && actionsRef.current[newStep.action]) {
        setTimeout(() => actionsRef.current[newStep.action]?.(), 50);
      }
      return { ...prev, currentStep: next };
    });
  }, []);

  const prevStep = useCallback(() => {
    setActiveTutorial((prev) => {
      if (!prev || prev.currentStep <= 0) return prev;
      const newIdx = prev.currentStep - 1;
      const newStep = prev.steps[newIdx];
      if (newStep && newStep.action && actionsRef.current[newStep.action]) {
        setTimeout(() => actionsRef.current[newStep.action]?.(), 50);
      }
      return { ...prev, currentStep: newIdx };
    });
  }, []);

  const skipTutorial = useCallback(() => {
    setActiveTutorial((prev) => {
      if (prev) {
        markCompleted(prev.id);
        // Ejecutar cleanup del paso actual
        const currentStep = prev.steps[prev.currentStep];
        if (currentStep && currentStep.cleanupAction && actionsRef.current[currentStep.cleanupAction]) {
          actionsRef.current[currentStep.cleanupAction]();
        }
      }
      return null;
    });
  }, []);

  const closeTutorial = useCallback(() => {
    setActiveTutorial((prev) => {
      if (prev) {
        const currentStep = prev.steps[prev.currentStep];
        if (currentStep && currentStep.cleanupAction && actionsRef.current[currentStep.cleanupAction]) {
          actionsRef.current[currentStep.cleanupAction]();
        }
      }
      return null;
    });
  }, []);

  const isTutorialCompleted = useCallback((tutorialId) => {
    return getCompleted().includes(tutorialId);
  }, []);

  const resetTutorial = useCallback((tutorialId) => {
    try {
      const completed = getCompleted().filter((id) => id !== tutorialId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(completed));
    } catch { /* ignorar */ }
  }, []);

  const resetAllTutorials = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignorar */ }
  }, []);

  return (
    <TutorialContext.Provider
      value={{
        activeTutorial,
        startTutorial,
        nextStep,
        prevStep,
        skipTutorial,
        closeTutorial,
        isTutorialCompleted,
        resetTutorial,
        resetAllTutorials,
        registerAction,
        unregisterAction,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error("useTutorial debe usarse dentro de TutorialProvider");
  return ctx;
};
